/**
 * AudioManager - Sistema de Audio para Mala Hierba
 * Versión híbrida mejorada
 */

export default class AudioManager {
  constructor(scene) {
    this.scene = scene;
    this.sounds = new Map();

    // Volúmenes
    this.musicVolume = 0.5;
    this.sfxVolume = 0.7;
    this.masterVolume = 1.0;

    // Estado
    this.isMuted = false;
    this.currentMusic = null;

    // Cargar configuración guardada
    this.loadSettings();
  }

  /**
   * Cargar configuración de localStorage
   */
  loadSettings() {
    try {
      const settings = JSON.parse(
        localStorage.getItem("audioSettings") || "{}",
      );
      if (settings.masterVolume !== undefined)
        this.masterVolume = settings.masterVolume;
      if (settings.musicVolume !== undefined)
        this.musicVolume = settings.musicVolume;
      if (settings.sfxVolume !== undefined) this.sfxVolume = settings.sfxVolume;
      if (settings.isMuted !== undefined) this.isMuted = settings.isMuted;
    } catch (e) {
      console.log("AudioManager: Configuración por defecto");
    }
  }

  /**
   * Guardar configuración en localStorage
   */
  saveSettings() {
    const settings = {
      masterVolume: this.masterVolume,
      musicVolume: this.musicVolume,
      sfxVolume: this.sfxVolume,
      isMuted: this.isMuted,
    };
    localStorage.setItem("audioSettings", JSON.stringify(settings));
  }

  /**
   * Inicializar sonidos después de preload
   * Esto precarga los sonidos en el Map para acceso rápido
   */
  init() {
    // Precargar efectos de sonido en el Map
    const sfxKeys = ["clean", "cut", "win", "lose"];

    sfxKeys.forEach((key) => {
      if (this.scene.cache.audio.exists(key)) {
        const sound = this.scene.sound.add(key);
        this.sounds.set(key, sound);
      } else {
        console.warn(`⚠️ Sonido '${key}' no encontrado en cache`);
      }
    });

    console.log("🎵 AudioManager inicializado");
  }

  // ============================================
  // MÚSICA
  // ============================================

  /**
   * Reproducir música del juego (game_theme.mp3 en loop)
   */
  playGameMusic() {
    if (this.isMuted) return;

    this.stopMusic(); // Detener música anterior

    if (this.scene.cache.audio.exists("game_theme")) {
      this.currentMusic = this.scene.sound.add("game_theme", {
        loop: true,
        volume: this.musicVolume * this.masterVolume,
      });
      this.currentMusic.play();
      console.log("🎵 Música de juego iniciada (loop)");
    } else {
      console.warn("⚠️ game_theme.mp3 no encontrado");
    }
  }

  /**
   * Reproducir música de victoria final (final_victory.mp3 sin loop)
   */
  playFinalVictoryMusic() {
    if (this.isMuted) return;

    this.stopMusic(); // Detener música anterior

    if (this.scene.cache.audio.exists("final_victory")) {
      this.currentMusic = this.scene.sound.add("final_victory", {
        loop: false, // ← NO loop, solo una vez
        volume: this.musicVolume * this.masterVolume,
      });
      this.currentMusic.play();
      console.log("🎵 Música de victoria final iniciada (sin loop)");
    } else {
      console.warn("⚠️ final_victory.mp3 no encontrado");
    }
  }

  /**
   * Pausar música actual
   */
  pauseMusic() {
    if (this.currentMusic && this.currentMusic.isPlaying) {
      this.currentMusic.pause();
    }
  }

  /**
   * Reanudar música
   */
  resumeMusic() {
    if (this.currentMusic && !this.currentMusic.isPlaying) {
      this.currentMusic.resume();
    }
  }

  /**
   * Detener música actual
   */
  stopMusic() {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic.destroy();
      this.currentMusic = null;
    }
  }

  // ============================================
  // EFECTOS DE SONIDO (SFX) - TUS ARCHIVOS
  // ============================================

  /**
   * Reproducir sonido de limpiar grieta (clean.mp3)
   */
  playClean() {
    this.playSFX("clean", 0.6);
  }

  /**
   * Reproducir sonido de cortar enredadera (cut.mp3)
   */
  playCut() {
    this.playSFX("cut", 0.6);
  }

  /**
   * Reproducir sonido de ganar ronda (win.mp3)
   */
  playWin() {
    this.playSFX("win", 0.7);
  }

  /**
   * Reproducir sonido de perder ronda (lose.mp3)
   */
  playLose() {
    this.playSFX("lose", 0.7);
  }

  // ============================================
  // MÉTODO GENÉRICO PARA REPRODUCIR SFX
  // ============================================

  /**
   * Reproducir efecto de sonido
   * @param {string} key - Clave del sonido
   * @param {number} volume - Volumen (0-1)
   */
  playSFX(key, volume = 0.5) {
    if (this.isMuted) return;

    const finalVolume = volume * this.sfxVolume * this.masterVolume;

    // Intentar reproducir desde el Map primero
    if (this.sounds.has(key)) {
      const sound = this.sounds.get(key);
      sound.play({ volume: finalVolume });
    }
    // Si no está en el Map, intentar reproducir directamente
    else if (this.scene.cache.audio.exists(key)) {
      this.scene.sound.play(key, { volume: finalVolume });
    } else {
      console.warn(`⚠️ Sonido '${key}' no encontrado`);
    }
  }

  // ============================================
  // CONTROLES DE VOLUMEN
  // ============================================

  /**
   * Establecer volumen general (0-1)
   */
  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.currentMusic) {
      this.currentMusic.setVolume(this.musicVolume * this.masterVolume);
    }
    this.saveSettings();
  }

  /**
   * Establecer volumen de música (0-1)
   */
  setMusicVolume(volume) {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (this.currentMusic) {
      this.currentMusic.setVolume(this.musicVolume * this.masterVolume);
    }
    this.saveSettings();
  }

  /**
   * Establecer volumen de SFX (0-1)
   */
  setSFXVolume(volume) {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.saveSettings();
  }

  // ============================================
  // MUTE / UNMUTE
  // ============================================

  /**
   * Mutear/Desmutear todo el audio
   */
  setMute(mute) {
    this.isMuted = mute;
    this.scene.sound.mute = mute;
    this.saveSettings();
  }

  /**
   * Toggle mute
   */
  toggleMute() {
    this.setMute(!this.isMuted);
    return this.isMuted;
  }

  /**
   * Obtener estado actual de mute
   */
  getMuteState() {
    return this.isMuted;
  }

  /**
   * Toggle música
   */
  toggleMusic() {
    if (this.currentMusic && this.currentMusic.isPlaying) {
      this.pauseMusic();
      return false;
    } else if (this.currentMusic) {
      this.resumeMusic();
      return true;
    }
    return false;
  }

  // ============================================
  // CLEANUP
  // ============================================

  /**
   * Detener todos los sonidos
   */
  stopAll() {
    this.scene.sound.stopAll();
    this.sounds.clear();
    this.currentMusic = null;
  }

  /**
   * Destruir el gestor
   */
  destroy() {
    this.stopMusic();

    this.sounds.forEach((sound) => {
      if (sound) sound.destroy();
    });

    this.sounds.clear();
    this.currentMusic = null;
    this.scene = null;
  }
}
